// @ts-nocheck
import { v4 } from 'uuid';
import { FileRecord, FileRecordOverview, FileStatusEnum, FileRecordTypeEnum, FilePath, FilePathNode } from '../types/file-record';
import { Group } from '../types/group';
import { ReconciliationWorksheet, WorksheetHistoryEventEnum, WorksheetHistoryRecord, WorksheetStatusEnum } from '../types/worksheet';
import { Firm } from '../types/firm';
import { Entity, EntityTypeEnum } from '../types/entity';
import { Workpaper, WorkpaperRoleEnum, WorkpaperSourceEnum, WorkpaperStatusEnum } from '../types/workpaper';
import { User, UserRoleEnum } from '../types/user';
import { dateReviver } from '../utilities/json-reviver';
import { OtherSchedule, OtherScheduleHistoryEvent, OtherScheduleHistoryRecord, OtherScheduleStatusEnum } from '../types/other-schedule';
import { WorksheetChecklistItemStatusEnum, WorksheetChecklistItem } from '../types/worksheet-checklist-item';
import { ChecklistItem } from '../types/checklist';
import { Industry } from '../types/industry';
import { Invitation, InvitationRoleEnum, InvitationStatusEnum } from '../types/invitation';
import { HistoryRecord } from '../types/history-record';
import { WorkpaperProcedure, WorkpaperProcedureStatusEnum } from '../types/workpaper-procedure';
import { WorksheetEntry, WorksheetEntryStatusEnum } from '../types/worksheet-entry';
import { Procedure, ProcedureTemplateOverview } from '../types/procedure';
import { InternalComment, InternalCommentOverview, InternalCommentStatusEnum } from '../types/internal-comment';
import { ClientQuery, ClientQueryOverview, ClientQueryStatusEnum } from '../types/client-query';
import { NatureEnum } from '../types/nature';
import { OtherScheduleEntry, OtherScheduleEntryStatusEnum } from '../types/other-schedule-entry';
import { Reply } from '../types/reply';
import { formatMixedCase } from '../utilities/formatting';

// JSON imports
import worksheetsSource from './data/worksheets.json';
import groupsSource from './data/groups.json';
import firmsSource from './data/firms.json';
import entitiesSource from './data/entities.json';
import workpapersSource from './data/workpapers.json';
import procedureTemplatesSource from './data/procedure-templates.json';
import usersSource from './data/users.json';
import checklistSource from './data/checklist.json';
import proceduresSource from './data/procedures.json';
import otherSchedulesSource from './data/other-schedules.json';
import industriesSource from './data/industries.json';

export class DB {
    private static Worksheets: ReconciliationWorksheet[] = [];
    private static Groups: Group[] = [];
    private static Files: FileRecord[] = [];
    private static Firms: Firm[] = [];
    private static Entities: Entity[] = [];
    private static Workpapers: Workpaper[] = [];
    private static ProcedureTemplates: ProcedureTemplateOverview[] = [];
    private static Users: User[] = [];
    private static Invitees: Invitation[] = [];
    private static Checklist: ChecklistItem[] = [];
    private static Procedures: Procedure[] = [];
    private static WorkpaperProcedures: WorkpaperProcedure[] = [];
    private static OtherSchedules: OtherSchedule[] = [];
    private static Industries: Industry[] = [];
    private static WorksheetEntries: WorksheetEntry[] = [];
    private static OtherScheduleEntries: OtherScheduleEntry[] = [];
    private static InternalComments: InternalComment[] = [];
    private static WorksheetChecklistItems: WorksheetChecklistItem[] = [];
    private static ClientQueries: ClientQuery[] = [];
    private static WorksheetHistory: WorksheetHistoryRecord[] = [];
    private static OtherScheduleHistory: OtherScheduleHistoryRecord[] = [];
    private static Replies: Reply[] = [];

    static Seed(): void {
        const worksheets = JSON.parse(JSON.stringify(worksheetsSource), dateReviver) as ReconciliationWorksheet[];
        const groups = JSON.parse(JSON.stringify(groupsSource), dateReviver) as Group[];
        const entities = JSON.parse(JSON.stringify(entitiesSource), dateReviver) as Entity[];
        const workpapers = JSON.parse(JSON.stringify(workpapersSource), dateReviver) as Workpaper[];
        const procedureTemplates = JSON.parse(JSON.stringify(procedureTemplatesSource), dateReviver) as ProcedureTemplateOverview[];
        const users = JSON.parse(JSON.stringify(usersSource), dateReviver) as User[];
        const firms = JSON.parse(JSON.stringify(firmsSource), dateReviver) as Firm[];
        const checklistTemplate = JSON.parse(JSON.stringify(checklistSource), dateReviver) as ChecklistItem[];
        const procedures = JSON.parse(JSON.stringify(proceduresSource), dateReviver) as Procedure[];
        const otherSchedules = JSON.parse(JSON.stringify(otherSchedulesSource), dateReviver) as OtherSchedule[];
        const industries = JSON.parse(JSON.stringify(industriesSource), dateReviver) as Industry[];

        this.Firms = firms;
        this.ProcedureTemplates = procedureTemplates;
        this.Checklist = checklistTemplate;
        this.Industries = industries;
        this.WorksheetEntries = [];
        this.InternalComments = [];
        this.ClientQueries = [];
        this.WorksheetHistory = [];
        this.OtherScheduleHistory = [];
        this.Replies = [];

        this.CreateFolder('85218fe4-5c42-44f8-b64e-88715e38b33d', 'ABC Accounting', true);

        worksheets.forEach((ws) => {
            this.Worksheets.push(ws);
            this.CreateFolder(
                ws.id,
                `${ws.code} - ${ws.description}`,
                true,
                [workpapers[0]?.id],
                ws.status === WorksheetStatusEnum.EXCLUDED
            );
            this.Checklist.filter((ct) => ct.reportCode === ws.reportCode).forEach((ci) => {
                this.WorksheetChecklistItems.push({
                    id: v4(),
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    description: ci.description,
                    reportCode: ci.reportCode,
                    checklistItemId: ci.id,
                    name: ci.name,
                    status: WorksheetChecklistItemStatusEnum.UNCHECKED,
                    worksheetId: ws.id,
                    sequence: '',
                    reference: '',
                });
            });
        });

        groups.forEach((g) => {
            this.Groups.push(g);
            this.CreateFolder(g.id, g.name, true, ['85218fe4-5c42-44f8-b64e-88715e38b33d']);
        });

        entities.forEach((e) => {
            this.Entities.push(e);
            this.CreateFolder(
                e.id,
                e.name,
                true,
                e.groups.length > 0 ? e.groups.map((g) => g.id) : ['85218fe4-5c42-44f8-b64e-88715e38b33d']
            );
        });

        workpapers.forEach((w) => {
            this.Workpapers.push(w);
            this.CreateFolder(w.id, w.name, true, [entities[0].id]);
            procedures.forEach((procedure) => {
                this.WorkpaperProcedures.push({
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    description: procedure.description,
                    name: procedure.name,
                    role: procedure.role,
                    // entityType: procedure.entityTypes,
                    procedureId: procedure.id,
                    reference: '',
                    workpaperProcedureCategoryId: '',
                    system: true,
                    id: v4(),
                    workpaperId: w.id,
                    sequence: 0,
                    status: WorkpaperProcedureStatusEnum.ACTIVE,
                });
            });
        });

        users.forEach((u) => {
            this.Users.push(u);
        });

        let id = v4();
        this.Invitees.push({
            id,
            firm: { id: '85218fe4-5c42-44f8-b64e-88715e38b33d', shortId: 'Fh-IY9vP', name: 'Cimplico' },
            email: 'demo@cimplico.com',
            role: UserRoleEnum.MANAGER,
            status: InvitationStatusEnum.PENDING,
            createdAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', firstName: 'Cimplico', lastName: 'Demo', profileHexColour: '' },
            expires: new Date(),
        });
        id = v4();
        this.Invitees.push({
            id,
            firm: { id: '85218fe4-5c42-44f8-b64e-88715e38b33d', shortId: 'Fh-IY9vP', name: 'Cimplico' },
            email: 'test@cimplico.com',
            role: UserRoleEnum.USER,
            status: InvitationStatusEnum.EXPIRED,
            createdAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', firstName: 'Cimplico', lastName: 'Demo', profileHexColour: '' },
            expires: new Date(),
        });

        otherSchedules.forEach((o) => {
            const entryId = v4();
            this.OtherScheduleEntries.push({
                id: entryId,
                description: 'test',
                createdAt: new Date(),
                updatedAt: new Date(),
                date: new Date(),
                files: [],
                reference: 1,
                sequence: 1,
                otherScheduleId: o.id,
                status: OtherScheduleEntryStatusEnum.ACTIVE,
            });
            this.OtherSchedules.push({ ...o, entries: [{ id: entryId }] });
            this.CreateFolder(o.id, o.name, false, [workpapers[0]?.id]);
        });

        id = v4();
        this.InternalComments.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawComment: 'Some title here to grab the readers attention that has some...',
            body: 'Vitae feugiat quam faucibus facilisis eu dictum faucibus. Urna integer imperdiet phasellus rhoncus imperdiet imperdiet. Lorem vel, congue vitae, dictum donec. Pharetra facilisi pellentesque mauris in ipsum, diam a malesuada.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: InternalCommentStatusEnum.RESOLVED,
            createdBy: { id: '36157c2a-4f03-48bf-856d-d9a2f1eaf931', firstName: '', lastName: '', profileHexColour: '' },
            worksheet: { id: 'e2bacdc6-2006-43c2-a5da-3c0e5f43b452', code: '', description: '' },
            replies: [
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
            ],
        });
        id = v4();
        this.InternalComments.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawComment: 'Culpa et dolorem enim et.',
            body: "There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable.",
            createdAt: new Date(),
            updatedAt: new Date(),
            status: InternalCommentStatusEnum.ACTIVE,
            createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
            otherSchedule: { id: 'feb72465-fcc4-4951-8843-aee3cc0ec43b', name: '' },
            replies: [],
        });
        id = v4();
        this.InternalComments.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawComment: 'Facilis accusantium provident quibusdam ipsum saepe inventore.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: InternalCommentStatusEnum.RESOLVED,
            createdBy: { id: '78e94165-4ef7-4d7d-a88c-5b878f317c02', firstName: '', lastName: '', profileHexColour: '' },
            replies: [],
        });
        id = v4();
        this.InternalComments.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawComment: 'Magnam qui et et voluptatem enim veritatis quia saepe.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: InternalCommentStatusEnum.RESOLVED,
            createdBy: { id: '78e94165-4ef7-4d7d-a88c-5b878f317c02', firstName: '', lastName: '', profileHexColour: '' },
            replies: [],
        });

        id = v4();
        this.ClientQueries.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawQuery: 'Some title here to grab the readers attention that has some...',
            body: 'Vitae feugiat quam faucibus facilisis eu dictum faucibus. Urna integer imperdiet phasellus rhoncus imperdiet imperdiet. Lorem vel, congue vitae, dictum donec. Pharetra facilisi pellentesque mauris in ipsum, diam a malesuada.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: ClientQueryStatusEnum.ACTIVE,
            createdBy: { id: '6c48c890-d2f6-48a6-8bb5-18c2329eeeec', firstName: '', lastName: '', profileHexColour: '' },
            worksheet: { id: '18e27517-81e0-437c-bc21-fbba8e30d6bb', code: '', description: '' },
            replies: [
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
                {
                    id: v4(),
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
            ],
        });
        id = v4();
        this.ClientQueries.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawQuery: 'Magnam qui et et voluptatem enim veritatis quia saepe.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: ClientQueryStatusEnum.RESOLVED,
            createdBy: { id: '78e94165-4ef7-4d7d-a88c-5b878f317c02', firstName: '', lastName: '', profileHexColour: '' },
            replies: [],
        });
        id = v4();
        this.ClientQueries.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawQuery: 'Culpa et dolorem enim et.',
            body: "There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful of model sentence structures, to generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc.",
            createdAt: new Date(),
            updatedAt: new Date(),
            status: ClientQueryStatusEnum.RESOLVED,
            createdBy: { id: 'd945f5b0-8770-4936-b02e-6397de019ffa', firstName: '', lastName: '', profileHexColour: '' },
            otherSchedule: { id: 'bf6234a9-d8bf-417d-8278-bb72cd000734', name: '' },
            replies: [
                {
                    id: '78e94165-4ef7-4d7d-a88c-5b878f317c02',
                    createdAt: new Date(),
                    createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
                    threadId: id,
                    updatedAt: new Date(),
                    rawReply:
                        'Odio libero eget nullam egestas purus sit. Aenean non, turpis adipiscing placerat. Fames elementum accumsan sem magna feugiat diam a habitasse ultrices. Nulla ut pharetra enim nunc eget.',
                },
            ],
        });
        id = v4();
        this.ClientQueries.push({
            id,
            workpaperId: 'bc72590e-341a-4ed3-bd1e-18b88c3cfe70',
            rawQuery: 'Facilis accusantium provident quibusdam ipsum saepe inventore.',
            createdAt: new Date(),
            updatedAt: new Date(),
            status: ClientQueryStatusEnum.RESOLVED,
            createdBy: { id: '6d3765fc-e5f7-441f-b449-c2b58386f7b7', firstName: '', lastName: '', profileHexColour: '' },
            replies: [],
        });
    }

    static GetFirms(): Firm[] {
        return this.Firms;
    }

    static GetEntities(): Entity[] {
        return this.Entities.map((entity) => {
            const groupIDs = entity.groups.map((g) => g.id);

            const groups = groupIDs.map((groupID) => {
                return this.GetGroup(groupID) as Group;
            });

            const user = this.Users.find((u) => u.id === entity.createdBy.id) as User;

            return {
                ...entity,
                createdBy: {
                    id: entity.createdBy.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    profileHexColour: user.profileHexColour,
                },
                groups: groups.map((group) => {
                    return {
                        id: group.id,
                        name: group.name,
                    };
                }),
            };
        });
    }

    static GetWorkpapers(): Workpaper[] {
        return this.Workpapers.map((workpaper) => {
            const entity = this.Entities.find((entity) => entity.id === workpaper.entity.id);

            const userIDs = workpaper.assigned.map((a) => a.id);

            const users = userIDs.map((userID) => {
                return this.GetUser(userID) as User;
            });
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + 30);
            return {
                ...workpaper,
                entity: { name: entity!.name, id: entity!.id, financialYearEndDay: 30, financialYearEndMonth: 6 },
                dueDate: dueDate.toISOString(),
                assigned: workpaper.assigned.map((assignee) => {
                    return {
                        ...assignee,
                        avatarUrl: users.find((user) => user.id === assignee.id)?.avatarUrl,
                        firstName: users.find((user) => user.id === assignee.id)!.firstName,
                        lastName: users.find((user) => user.id === assignee.id)!.lastName,
                        profileHexColour: users.find((user) => user.id === assignee.id)!.profileHexColour,
                    };
                }),
            };
        });
    }

    static GetProcedureTemplates(): ProcedureTemplateOverview[] {
        return this.ProcedureTemplates.map((pt) => {
            const user = this.Users.find((u) => u.id === pt.createdBy.id) as User;
            return {
                ...pt,
                createdBy: {
                    id: pt.createdBy.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    profileHexColour: user.profileHexColour,
                },
            };
        });
    }

    static GetWorkpaperProcedures(workpaperId: string): WorkpaperProcedure[] {
        return this.WorkpaperProcedures.filter((jp) => jp.workpaperId === workpaperId).map((jp) => {
            if (!jp.userAction) {
                return jp;
            }
            const user = this.Users.find((u) => u.id === jp.userAction?.id) as User;
            jp.userAction.firstName = user.firstName;
            jp.userAction.lastName = user.lastName;
            jp.userAction.avatarUrl = user.avatarUrl;
            return jp;
        });
    }

    static GetWorkpaperProcedure(workpaperId: string, procedureId: string): WorkpaperProcedure | undefined {
        this.WorkpaperProcedures = this.WorkpaperProcedures.filter((jp) => jp.workpaperId === workpaperId).map((jp) => {
            if (!jp.userAction) {
                return jp;
            }
            const user = this.Users.find((u) => u.id === jp.userAction?.id) as User;
            jp.userAction.firstName = user.firstName;
            jp.userAction.lastName = user.lastName;
            jp.userAction.avatarUrl = user.avatarUrl;
            return jp;
        });
        return this.WorkpaperProcedures.find((jp) => jp.id === procedureId);
    }

    static GetGroups(): Group[] {
        return this.Groups.map((g) => {
            const workpapers: { id: string; name: string; entity: { id: string; name: string } }[] = [];

            g.entities.forEach((e) => {
                const entity = this.GetEntity(e.id) as Entity;
                entity.activeWorkpapers.forEach((wp) => {
                    const workpaper = this.GetWorkpaper(wp.id) as Workpaper;
                    if (workpaper.status === WorkpaperStatusEnum.ACTIVE) {
                        workpapers.push({ id: workpaper.id, name: workpaper.name, entity: { id: entity.id, name: entity.name } });
                    }
                });
            });
            return { ...g, activeWorkpapers: workpapers, description: undefined };
        });
    }

    static GetGroup(id: string): Group | undefined {
        return this.Groups.find((g) => g.id === id);
    }

    static GetEntity(id: string): Entity | undefined {
        const entity = this.Entities.find((e) => e.id === id);
        if (!entity) {
            return;
        }
        const user = this.Users.find((u) => u.id === entity.createdBy.id) as User;
        return {
            ...entity,
            createdBy: {
                id: entity.createdBy.id,
                firstName: user.firstName,
                lastName: user.lastName,
                profileHexColour: user.profileHexColour,
            },
        };
    }

    static GetWorkpaper(id: string): Workpaper | undefined {
        const workpaper = this.Workpapers.find((w) => w.id === id);
        if (!workpaper) {
            return;
        }

        // const progressBreakdown: Workpaper['progressBreakdown'] = {
        //     procedures: {
        //         // percentComplete: this.CalcJobProceduresPercentComplete(job.id),
        //         // totalItems: this.GetJobProcedures(job.id).length,
        //         // fields: Object.keys(JobProcedureStatusEnum).map((key) => {
        //         //     return {
        //         //         label: key as JobProcedureStatusEnum,
        //         //         count: this.GetJobProcedures(job.id).filter((jp) => jp.status === key).length,
        //         //         percent: this.CalcJobProcedureFieldProgress(job.id, key as JobProcedureStatusEnum),
        //         //     };
        //         // }),
        //         active: this.GetWorkpaperProcedures(workpaper.id).filter((p) => p.status === WorkpaperProcedureStatusEnum.ACTIVE).length,
        //         checked: this.GetWorkpaperProcedures(workpaper.id).filter((p) => p.status === WorkpaperProcedureStatusEnum.CHECKED).length,
        //         notApplicable: this.GetWorkpaperProcedures(workpaper.id).filter(
        //             (p) => p.status === WorkpaperProcedureStatusEnum.NOT_APPLICABLE
        //         ).length,
        //     },
        //     worksheets: {
        //         reviewed: this.GetWorksheets().filter((w) => w.status === WorksheetStatusEnum.REVIEWED).length,
        //         excluded: this.GetWorksheets().filter((w) => w.status === WorksheetStatusEnum.EXCLUDED).length,
        //         awaitingReview: this.GetWorksheets().filter((w) => w.status === WorksheetStatusEnum.AWAITING_REVIEW).length,
        //         unprepared: this.GetWorksheets().filter((w) => w.status === WorksheetStatusEnum.UNPREPARED).length,

        //         // percentComplete: this.CalcWorksheetPercentComplete(job.id),
        //         // totalItems: this.GetWorksheets().filter((ws) => ws.jobId === job.id).length,
        //         // fields: Object.keys(WorksheetStatusEnum).map((key) => {
        //         //     return {
        //         //         label: key as WorksheetStatusEnum,
        //         //         count: this.GetWorksheets().filter((ws) => ws.status === key && ws.jobId === job.id).length,
        //         //         percent: this.CalcWorksheetFieldProgress(job.id, key as WorksheetStatusEnum),
        //         //     };
        //         // }),
        //     },
        //     otherSchedules: {
        //         reviewed: this.GetOtherSchedules().filter((o) => o.status === OtherScheduleStatusEnum.REVIEWED).length,
        //         archived: this.GetOtherSchedules().filter((o) => o.status === OtherScheduleStatusEnum.ARCHIVED).length,
        //         awaitingReview: this.GetOtherSchedules().filter((o) => o.status === OtherScheduleStatusEnum.AWAITING_REVIEW).length,
        //         // percentComplete: this.CalcOtherSchedulePercentComplete(job.id),
        //         // totalItems: this.GetOtherSchedules().filter((os) => os.jobId === job.id).length,
        //         // fields: Object.keys(OtherScheduleStatusEnum).map((key) => {
        //         //     return {
        //         //         label: key as OtherScheduleStatusEnum,
        //         //         count: this.GetOtherSchedules().filter((os) => os.status === key && os.jobId === job.id).length,
        //         //         percent: this.CalcOtherScheduleFieldProgress(job.id, key as OtherScheduleStatusEnum),
        //         //     };
        //         // }),
        //     },
        //     internalComments: {
        //         resolved: this.GetInternalComments(workpaper.id).filter((i) => i.status === InternalCommentStatusEnum.RESOLVED).length,
        //         active: this.GetInternalComments(workpaper.id).filter((i) => i.status === InternalCommentStatusEnum.RESOLVED).length,
        //         // percentComplete: this.CalcInternalCommentPercentComplete(job.id),
        //         // totalItems: this.GetInternalComments(job.id).length,
        //         // fields: Object.keys(InternalCommentStatusEnum).map((key) => {
        //         //     return {
        //         //         label: key as InternalCommentStatusEnum,
        //         //         count: this.GetInternalComments(job.id).filter((c) => c.status === key).length,
        //         //         percent: this.CalcInternalCommentFieldProgress(job.id, key as InternalCommentStatusEnum),
        //         //     };
        //         // }),
        //     },
        //     clientQueries: {
        //         resolved: this.GetClientQueries(workpaper.id).filter((c) => c.status === ClientQueryStatusEnum.RESOLVED).length,
        //         active: this.GetClientQueries(workpaper.id).filter((c) => c.status === ClientQueryStatusEnum.ACTIVE).length,
        //         draft: this.GetClientQueries(workpaper.id).filter((c) => c.status === ClientQueryStatusEnum.DRAFT).length,

        //         // percentComplete: this.CalcInternalCommentPercentComplete(job.id),
        //         //     percentComplete: this.CalcClientQueryPercentComplete(job.id),
        //         //     totalItems: this.GetClientQueries(job.id).length,
        //         //     fields: Object.keys(ClientQueryStatusEnum).map((key) => {
        //         //         return {
        //         //             label: key as ClientQueryStatusEnum,
        //         //             count: this.GetClientQueries(job.id).filter((q) => q.status === key).length,
        //         //             percent: this.CalcClientQueryFieldProgress(job.id, key as ClientQueryStatusEnum),
        //         //         };
        //         //     }),
        //     },
        // };

        const users = workpaper.assigned
            .map((a) => a.id)
            .map((userID) => {
                return this.GetUser(userID) as User;
            });
        const entity = this.Entities.find((entity) => entity.id === workpaper.entity.id);
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 30);

        return {
            ...workpaper,
            entity: { name: entity!.name, id: entity!.id, financialYearEndDay: 30, financialYearEndMonth: 6 },
            assigned: workpaper.assigned.map((assignee) => {
                return {
                    ...assignee,
                    avatar: users.find((user) => user.id === assignee.id)?.avatarUrl,
                    firstName: users.find((user) => user.id === assignee.id)!.firstName,
                    lastName: users.find((user) => user.id === assignee.id)!.lastName,
                };
            }),
            dueDate: dueDate.toISOString(),
            // progressBreakdown,
        };
    }

    static CalcWorkpaperProceduresPercentComplete(workpaperId: string): number {
        const procedures = this.GetWorkpaperProcedures(workpaperId);
        if (procedures.length === 0) {
            return 100;
        }
        const complete = procedures.filter((p) => p.status !== WorkpaperProcedureStatusEnum.ACTIVE);
        return Math.floor((complete.length / procedures.length) * 100);
    }

    static CalcWorkpaperProcedureFieldProgress(workpaperId: string, field: WorkpaperProcedureStatusEnum): number {
        const procedures = this.GetWorkpaperProcedures(workpaperId);
        if (procedures.length === 0) {
            return 100;
        }
        const complete = procedures.filter((p) => p.status === field);

        return Math.floor((complete.length / procedures.length) * 100);
    }

    static CalcWorksheetPercentComplete(workpaperId: string): number {
        const worksheets = this.GetWorksheets().filter((ws) => ws.workpaperId === workpaperId);
        if (worksheets.length === 0) {
            return 100;
        }
        const complete = worksheets.filter(
            (ws) => ws.status === WorksheetStatusEnum.REVIEWED || ws.status === WorksheetStatusEnum.EXCLUDED
        );
        return Math.floor((complete.length / worksheets.length) * 100);
    }

    static CalcWorksheetFieldProgress(workpaperId: string, field: WorksheetStatusEnum): number {
        const worksheets = this.GetWorksheets().filter((ws) => ws.workpaperId === workpaperId);
        if (worksheets.length === 0) {
            return 100;
        }
        const complete = worksheets.filter((ws) => ws.status === field);

        return Math.floor((complete.length / worksheets.length) * 100);
    }

    static CalcOtherSchedulePercentComplete(workpaperId: string): number {
        const schedules = this.GetOtherSchedules().filter((os) => os.workpaperId === workpaperId);
        if (schedules.length === 0) {
            return 100;
        }
        const complete = schedules.filter(
            (os) => os.status === OtherScheduleStatusEnum.REVIEWED || os.status === OtherScheduleStatusEnum.ARCHIVED
        );
        return Math.floor((complete.length / schedules.length) * 100);
    }

    static CalcOtherScheduleFieldProgress(workpaperId: string, field: OtherScheduleStatusEnum): number {
        const schedules = this.GetOtherSchedules().filter((os) => os.workpaperId === workpaperId);
        if (schedules.length === 0) {
            return 100;
        }
        const complete = schedules.filter((os) => os.status === field);

        return Math.floor((complete.length / schedules.length) * 100);
    }

    static CalcInternalCommentPercentComplete(workpaperId: string): number {
        const comments = this.GetInternalComments(workpaperId);
        if (comments.length === 0) {
            return 100;
        }

        const complete = comments.filter((c) => c.status === InternalCommentStatusEnum.RESOLVED);
        return Math.floor((complete.length / comments.length) * 100);
    }

    static CalcInternalCommentFieldProgress(workpaperId: string, field: InternalCommentStatusEnum): number {
        const comments = this.GetInternalComments(workpaperId);
        if (comments.length === 0) {
            return 100;
        }
        const complete = comments.filter((c) => c.status === field);

        return Math.floor((complete.length / comments.length) * 100);
    }

    static CalcClientQueryPercentComplete(workpaperId: string): number {
        const queries = this.GetClientQueries(workpaperId);
        if (queries.length === 0) {
            return 100;
        }

        const complete = queries.filter((c) => c.status === ClientQueryStatusEnum.RESOLVED);
        return Math.floor((complete.length / queries.length) * 100);
    }

    static CalcClientQueryFieldProgress(workpaperId: string, field: ClientQueryStatusEnum): number {
        const queries = this.GetClientQueries(workpaperId);
        if (queries.length === 0) {
            return 100;
        }
        const complete = queries.filter((c) => c.status === field);

        return Math.floor((complete.length / queries.length) * 100);
    }

    static GetIndustries(): Industry[] {
        return this.Industries.map((i) => i);
    }

    static CreateGroup(name: string, entities: string[], description?: string): Group {
        // Must have a name
        if (!name) {
            throw new Error('A group must have a name.');
        }
        // Find an existing group with the name
        if (this.Groups.find((group) => group.name.toLowerCase() === name.toLowerCase())) {
            throw new Error('A group with this name already exists.');
        }

        const id = v4();
        const group: Group = {
            firmId: '85218fe4-5c42-44f8-b64e-88715e38b33d',
            id,
            name,
            description,
            entities: entities as unknown as { id: string; name: string; isArchived: boolean }[],
            activeWorkpapers: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            integrations: {},
        };
        this.Groups.push(group);

        entities.forEach((ent) => {
            const entity = this.Entities.find((e) => e.id === ent);
            if (entity) {
                entity.groups.push({ id: group.id, name: group.name });
            }
        });

        return group;
    }

    static DeleteGroup(groupId: string): void {
        const group = this.Groups.find((g) => g.id === groupId);

        this.Entities = this.Entities.map((entity) => {
            return {
                ...entity,
                groups: entity.groups.filter((g) => g.id !== groupId),
            };
        });

        if (!group) {
            return;
        }

        const index = this.Groups.indexOf(group);
        if (index > -1) {
            this.Groups.splice(index, 1);
        }
    }

    static CreateEntity(
        name: string,
        groups: string[],
        type: EntityTypeEnum,
        financialYearEndDay: number,
        financialYearEndMonth: number,
        industry?: string
    ): Entity {
        // Must have a name
        if (!name) {
            throw new Error('An entity must have a name.');
        }
        // Find an existing entity with the name
        if (this.Entities.find((entity) => entity.name.toLowerCase() === name.toLowerCase())) {
            throw new Error('An entity with this name already exists.');
        }
        // Must have a year end month
        if (!financialYearEndMonth) {
            throw new Error('A financial year end month must be selected.');
        }
        // Must have a year end day
        if (!financialYearEndDay) {
            throw new Error('A financial year end day must be selected.');
        }
        // Must have a type
        if (!type) {
            throw new Error('An entity must have a type.');
        }
        // Must have an industry if entity type is not a trust
        if (type !== EntityTypeEnum.TRUST && !industry) {
            throw new Error('An entity must belong to an industry.');
        }

        const id = v4();
        const entity: Entity = {
            firmId: '85218fe4-5c42-44f8-b64e-88715e38b33d',
            id,
            name,
            isArchived: false,
            type,
            industry,
            activeWorkpapers: [],
            updatedAt: new Date(),
            createdAt: new Date(),
            financialYearEndDay,
            financialYearEndMonth,
            createdBy: { id: 'd945f5b0-8770-4936-b02e-6397de019ffa', lastName: '', firstName: '', profileHexColour: '' },
            groups: groups.length === 0 ? [] : [{ id: groups[0], name: '' }],
            integrations: {},
            subscriptionActive: false,
        };
        this.Entities.push(entity);

        groups.forEach((groupId) => {
            const group = this.Groups.find((g) => g.id === groupId);
            if (group) {
                group.entities.push({ id: entity.id, name: entity.name, isArchived: entity.isArchived });
            }
        });

        return entity;
    }

    static DeleteEntity(entityId: string): void {
        const entity = this.Entities.find((e) => e.id === entityId);

        this.Groups = this.Groups.map((group) => {
            return {
                ...group,
                entities: group.entities.filter((e) => e.id !== entityId),
            };
        });

        if (!entity) {
            return;
        }

        this.Entities = this.Entities.filter((e) => e.id !== entityId);
    }

    static CreateWorkpaper(
        entity: string,
        provider: WorkpaperSourceEnum,
        periodEnd: string,
        dueDate: string,
        preparer: [],
        reviewer: [],
        approver: [],
        name: string
    ): Workpaper {
        // Must have an entity
        if (!entity) {
            throw new Error('An entity must be selected.');
        }

        const id = v4();
        const workpaper: Workpaper = {
            id,
            entity: { id: entity[0], name: '', financialYearEndDay: 30, financialYearEndMonth: 6 },
            procedureTemplateId: '',
            checklistId: '',
            provider,
            status: WorkpaperStatusEnum.ACTIVE,
            name,
            periodEnd,
            dueDate,
            private: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: '',
            completedAt: new Date(),
            completedBy: '',
            assigned: [],
            progressPercent: 0,
            integrations: null,
        };
        this.Workpapers.push(workpaper);

        return workpaper;
    }

    static GetUsers(): User[] {
        return this.Users.map((e) => e);
    }

    static GetUser(id: string): User | undefined {
        return this.Users.find((u) => u.id === id);
    }

    static CreateInvitation(email: string, role: UserRoleEnum): Invitation {
        // Must have an email
        if (!email) {
            throw new Error('A user must have an email.');
        }

        // Find an existing invitee with the email
        if (this.Invitees.find((invitee) => invitee.email.toLowerCase() === email.toLowerCase())) {
            throw new Error('A user with this email already exists.');
        }

        const id = v4();
        const invitee: Invitation = {
            id,
            email,
            role: role as InvitationRoleEnum,
            status: InvitationStatusEnum.PENDING,
            createdAt: new Date(),
            createdBy: {
                id: '36157c2a-4f03-48bf-856d-d9a2f1eaf931',
                firstName: 'Livia',
                lastName: 'Septimus',
                profileHexColour: '#a855f7',
            },
            firm: { id: '', shortId: '', name: '' },
            expires: new Date(),
        };
        this.Invitees.push(invitee);

        return invitee;
    }

    static GetInvitations(): Invitation[] {
        return this.Invitees.map((i) => i);
    }

    static DeleteInvitation(id: string): void {
        this.Invitees = this.Invitees.filter((i) => i.id !== id);
    }

    static UpdateWorkpaperProceduresStatus(
        id: string,
        workpaperProcedureStatus: WorkpaperProcedureStatusEnum,
        notApplicableReason?: string
    ): void {
        this.WorkpaperProcedures = this.WorkpaperProcedures.map((workpaperProcedure) => {
            if (id !== workpaperProcedure.id) {
                return workpaperProcedure;
            }
            return {
                ...workpaperProcedure,
                status: workpaperProcedureStatus,
                updatedAt: new Date(),
                notApplicableReason: notApplicableReason,
                userAction:
                    workpaperProcedureStatus === WorkpaperProcedureStatusEnum.ACTIVE
                        ? undefined
                        : {
                              id: '78e94165-4ef7-4d7d-a88c-5b878f317c02',
                              firstName: '',
                              lastName: '',
                              profileHexColour: '',
                              date: new Date(),
                          },
            };
        });
    }

    static UpdateWorkpaperProceduresDetails(
        id: string,
        name: string,
        role: WorkpaperRoleEnum,
        description?: string,
        entityType?: EntityTypeEnum | null
    ): void {
        this.WorkpaperProcedures = this.WorkpaperProcedures.map((workpaperProcedure) => {
            if (id !== workpaperProcedure.id) {
                return workpaperProcedure;
            }
            return {
                ...workpaperProcedure,
                updatedAt: new Date(),
                status: WorkpaperProcedureStatusEnum.ACTIVE,
                name: name,
                role: role,
                description: description,
                entityType,
            };
        });
    }

    static CreateWorkpaperProcedure(
        workpaperId: string,
        name: string,
        role: WorkpaperRoleEnum,
        description?: string,
        entityType?: EntityTypeEnum
    ): WorkpaperProcedure {
        // Must have a name
        if (!name) {
            throw new Error('A procedure must have a name.');
        }
        // Find an existing procedure with the name
        if (this.WorkpaperProcedures.find((jp) => jp.name.toLowerCase() === name.toLowerCase())) {
            throw new Error('A procedure with this name already exists.');
        }
        // Must have a role
        if (!role) {
            throw new Error('A procedure must have a role.');
        }

        const id = v4();
        const workpaperProcedure: WorkpaperProcedure = {
            id,
            workpaperId: workpaperId,
            workpaperProcedureCategoryId: '',
            procedureId: '',
            reference: '',
            system: false,
            name,
            description,
            role,
            entityType,
            sequence: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            status: WorkpaperProcedureStatusEnum.ACTIVE,
        };
        this.WorkpaperProcedures.push(workpaperProcedure);

        return workpaperProcedure;
    }

    static DeleteWorkpaperProcedure(id: string): void {
        const workpaperProcedure = this.WorkpaperProcedures.find((jp) => jp.id === id);

        if (!workpaperProcedure) {
            return;
        }

        const index = this.WorkpaperProcedures.indexOf(workpaperProcedure);
        if (index > -1) {
            this.WorkpaperProcedures.splice(index, 1);
        }
    }

    static GetWorksheets(): ReconciliationWorksheet[] {
        return this.Worksheets.map((worksheet) => {
            const userIDs = worksheet.assigned.map((ws) => ws.id);

            const users = userIDs.map((userID) => {
                return this.GetUser(userID) as User;
            });

            const workpaper = this.Workpapers.find((workpaper) => workpaper.id === worksheet.workpaperId) as Workpaper;

            return {
                ...worksheet,
                internalComments: this.InternalComments.filter((ic) => ic?.worksheet?.id === worksheet.id).map((ic) => ({
                    id: ic.id,
                    status: ic.status,
                })),
                clientQueries: this.ClientQueries.filter((cq) => cq?.worksheet?.id === worksheet.id).map((cq) => ({
                    id: cq.id,
                    status: cq.status,
                })),
                assigned: worksheet.assigned.map((assignee) => {
                    return {
                        ...assignee,
                        avatarUrl: users.find((user) => user.id === assignee.id)?.avatarUrl,
                        firstName: users.find((user) => user.id === assignee.id)!.firstName,
                        lastName: users.find((user) => user.id === assignee.id)!.lastName,
                        profileHexColour: users.find((user) => user.id === assignee.id)!.profileHexColour,
                        role: workpaper.assigned.find((w) => w.id === assignee.id)?.role,
                    };
                }),
            };
        });
    }

    static UpdateWorksheet(id: string, fields: Partial<ReconciliationWorksheet>): void {}

    static UpdateWorksheetAssignees(id: string, assignees: string[]): void {
        this.Worksheets = this.Worksheets.map((ws) => {
            if (ws.id !== id) {
                return ws;
            }
            return { ...ws, assigned: assignees.map((a) => ({ id: a, firstName: '', lastName: '', profileHexColour: '' })) };
        });
    }

    static UpdateOtherScheduleAssignees(id: string, assignees: string[]): void {
        this.OtherSchedules = this.OtherSchedules.map((os) => {
            if (os.id !== id) {
                return os;
            }
            return { ...os, assigned: assignees.map((a) => ({ id: a, firstName: '', lastName: '', profileHexColour: '' })) };
        });
    }

    static GetWorksheet(id: string): ReconciliationWorksheet | undefined {
        const worksheet = this.Worksheets.find((ws) => ws.id === id);
        if (!worksheet) {
            return;
        }

        const entries = this.GetWorksheetEntries(worksheet.id);
        worksheet.entries = entries.map((e) => ({ amount: e.amount, nature: e.nature, id: e.id }));

        const fileRecords = this.Files.filter((f) => f.parentFolderId?.includes(worksheet.id));

        worksheet.files = fileRecords.map((fr) => {
            return {
                id: fr.id,

                entries: entries
                    .filter((e) => e.files.includes(fr.id))
                    .map((e) => {
                        return { id: e.id, reference: e.reference };
                    }),
            };
        });

        worksheet.checklist = this.WorksheetChecklistItems.filter((wci) => wci.worksheetId === id).map((wci) => ({
            id: wci.id,
            status: wci.status,
        }));

        worksheet.assigned = worksheet.assigned.map((a) => {
            const user = this.Users.find((u) => u.id === a.id) as User;
            const workpaper = this.Workpapers.find((workpaper) => workpaper.id === worksheet.workpaperId) as Workpaper;
            return {
                id: a.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
                role: workpaper.assigned.find((w) => w.id === a.id)?.role,
            };
        });

        return worksheet;
    }

    static GetWorksheetHistory(id: string): WorksheetHistoryRecord[] {
        return this.WorksheetHistory.filter((wh) => wh.worksheetId === id);
    }
    // static GetChecklistItem(id: string): ChecklistItem | undefined {
    //     return this.Checklist.find((c) => c.id === id);
    // }

    static GetWorksheetChecklist(worksheetId: string): WorksheetChecklistItem[] {
        return this.WorksheetChecklistItems.filter((wci) => wci.worksheetId === worksheetId);
    }

    static GetOtherSchedules(): OtherSchedule[] {
        return this.OtherSchedules.map((schedule) => {
            schedule.internalComments = this.InternalComments.filter((ic) => ic?.otherSchedule?.id === schedule.id).map((ic) => ({
                id: ic.id,
                status: ic.status,
            }));

            schedule.clientQueries = this.ClientQueries.filter((cq) => cq?.otherSchedule?.id === schedule.id).map((cq) => ({
                id: cq.id,
                status: cq.status,
            }));

            schedule.assigned = schedule.assigned.map((a) => {
                const user = this.Users.find((u) => u.id === a.id) as User;
                return {
                    id: a.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    avatarUrl: user.avatarUrl,
                    profileHexColour: user.profileHexColour,
                };
            });
            return schedule;
        });
    }

    static GetOtherSchedule(id: string): OtherSchedule | undefined {
        const schedule = this.OtherSchedules.find((ws) => ws.id === id);
        if (!schedule) {
            return;
        }

        const entries = this.GetOtherScheduleEntries(schedule.id);
        schedule.entries = entries.map((e) => ({ id: e.id }));

        const fileRecords = this.Files.filter((f) => f.parentFolderId?.includes(schedule.id));

        schedule.files = fileRecords.map((fr) => {
            return {
                id: fr.id,
                entries: entries
                    .filter((e) => e.files.includes(fr.id))
                    .map((e) => {
                        return { id: e.id, reference: e.reference };
                    }),
            };
        });

        schedule.clientQueries = this.ClientQueries.filter((cq) => cq?.otherSchedule?.id === schedule.id).map((cq) => ({
            id: cq.id,
            status: cq.status,
        }));

        schedule.assigned = schedule.assigned.map((a) => {
            const user = this.Users.find((u) => u.id === a.id) as User;
            return {
                id: a.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            };
        });

        schedule.assigned = schedule.assigned.map((a) => {
            const user = this.Users.find((u) => u.id === a.id) as User;
            return {
                id: a.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            };
        });
        return schedule;
    }

    static GetOtherScheduleHistory(id: string): OtherScheduleHistoryRecord[] {
        return this.OtherScheduleHistory.filter((sh) => sh.otherScheduleId === id);
    }

    static GetAllFileRecords(): FileRecordOverview[] {
        return this.Files.map((file) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let source: any;
            if (file.recordType === FileRecordTypeEnum.REFERENCE) {
                const original = this.Files.find((f) => f.id === file.source?.id);

                source = {};
                source.id = original!.id;
                source.name = original!.name;
                source.link = original?.link;
                source.recordType = original!.recordType;
                source.locked = false;
            }
            return {
                name: file.name,
                createdAt: file.createdAt,
                updatedAt: file.updatedAt,
                source,
                parentFolderId: file.parentFolderId,
                id: file.id,
                recordType: file.recordType,
                status: file.status,
                itemCount: file.itemCount,
                system: file.systemRecord,
                link: file.link,
                hidden: file.hidden,
                locked: false,
            };
        });
    }

    static CreateOtherSchedule(workpaperId: string, name: string, description?: string): OtherSchedule {
        // Must have a name
        if (!name) {
            throw new Error('A schedule must have a name.');
        }
        // Find an existing schedule with the name
        if (this.OtherSchedules.find((os) => os.name.toLowerCase() === name.toLowerCase())) {
            throw new Error('A schedule with this name already exists.');
        }

        const id = v4();
        const otherSchedule: OtherSchedule = {
            id,
            workpaperId,
            name,
            description,
            status: OtherScheduleStatusEnum.AWAITING_REVIEW,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: { id: '5724bcb4-2768-4361-b963-14dc9ff1b246', firstName: '', lastName: '', profileHexColour: '' },
            entries: [],
            files: [],
            assigned: [],
            clientQueries: [],
            internalComments: [],
        };
        this.OtherSchedules.push(otherSchedule);

        return otherSchedule;
    }

    static UpdateFileRecord(id: string, record: Partial<FileRecord>): FileRecord | undefined {
        const existingRecord = this.Files.find((f) => f.id === id);

        if (!existingRecord) {
            throw new Error('No record exits');
        }

        this.Files = this.Files.map((f) => {
            if (f.id === id) {
                return { ...f, ...record };
            }
            return f;
        });

        return this.Files.find((f) => f.id === id);
    }

    static AddFileRecordHistory(id: string, historyRecord: HistoryRecord): void {
        const record = this.Files.find((r) => r.id === id);
        if (record) {
            record.history.push(historyRecord);
        }
    }

    static GetFileRecords(childId?: string, folderId?: string): FileRecordOverview[] {
        const childRecord = this.Files.find((f) => f.id === childId);

        /**
         * This is a prototype algorithm that only works for the root directory.
         *
         * It first gets all groups and if the current workpaper id has that group as a parent, the group will be returned.
         * It then finds all entity folders for the group.
         *  - If the workpaper has that entity as a parent, the group will be returned.
         *
         * Only groups that have the workpaper as a grandchild will be returned.
         */
        if (!folderId) {
            if (!childRecord) {
                return [];
            }
            const folders = [];
            const groups = this.Files.filter((f) => !f.parentFolderId);

            for (const group of groups) {
                if (childRecord.parentFolderId?.includes(group.id)) {
                    folders.push(group);
                }
                const entityRecords = this.Files.filter((f) => f.parentFolderId?.includes(group.id));

                for (const entityRecord of entityRecords) {
                    if (childRecord.parentFolderId?.includes(entityRecord.id)) {
                        folders.push(group);
                    }
                }
            }

            return folders.map((file) => {
                return {
                    locked: false,
                    name: file.name,
                    createdAt: file.createdAt,
                    updatedAt: file.updatedAt,
                    source: file.source,
                    parentFolderId: file.parentFolderId,
                    id: file.id,
                    recordType: file.recordType,
                    status: file.status,
                    itemCount: file.itemCount,
                    system: file.systemRecord,
                    link: file.link,
                    mimeType: file.mimeType,
                    hidden: file.hidden,
                };
            });
        }

        const children = this.Files.filter((file) => file.parentFolderId?.includes(folderId));

        return children.map((file) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let source: any;
            if (file.recordType === FileRecordTypeEnum.REFERENCE) {
                const original = this.Files.find((f) => f.id === file.source?.id);
                source = {};
                source.id = original!.id;
                source.name = original!.name;
                source.link = original?.link;
                source.recordType = original!.recordType;
            }

            return {
                locked: false,
                name: file.name,
                createdAt: file.createdAt,
                updatedAt: file.updatedAt,
                source,
                parentFolderId: file.parentFolderId,
                id: file.id,
                recordType: file.recordType,
                status: file.status,
                itemCount: file.itemCount,
                system: file.systemRecord,
                link: file.link,
                mimeType: file.mimeType,
                hidden: file.hidden,
            };
        });
    }

    static DeleteFileRecord(id: string): void {
        this.Files = this.Files.filter((f) => f.id !== id);
    }

    static GetFileRecord(id: string): FileRecord | undefined {
        const record = this.Files.find((f) => f.id === id);
        if (!record) {
            return;
        }

        if (record.recordType === FileRecordTypeEnum.REFERENCE) {
            const original = this.Files.find((f) => f.id === record.source?.id);
            return {
                ...record,
                source: { id: original!.id, recordType: original!.recordType, name: original!.name, link: original?.link },
            };
        }
        return record;
    }

    static GetFilePath(baseFolderId?: string): FilePath {
        if (baseFolderId === undefined) {
            return [
                {
                    level: 0,
                    folders: [
                        {
                            name: '',
                            id: '',
                        },
                    ],
                },
            ];
        }

        const base = DB.GetFileRecord(baseFolderId);

        const hierarchy: FilePath = [];

        if (!base) {
            return [
                {
                    level: 0,
                    folders: [
                        {
                            name: '',
                            id: '',
                        },
                    ],
                },
            ];
        }

        hierarchy.push({ level: 0, folders: [{ name: base.name, id: base.id }] });

        let level = 1;
        if (base.parentFolderId) {
            let parents = base.parentFolderId;
            while (parents.length > 0) {
                const tmpParents: string[] = [];
                const node: FilePathNode = {
                    level,
                    folders: [],
                };

                parents.forEach((id) => {
                    const folder = DB.GetFileRecord(id);

                    if (folder) {
                        node.folders.push({ name: folder.name, id: folder.id });
                        if (folder.parentFolderId) {
                            tmpParents.push(...folder.parentFolderId);
                        }
                    }
                });

                level += 1;
                hierarchy.push(node);
                parents = Array.from(new Set(tmpParents));
            }
        }

        // for (const id of base.parentFolderId) {
        //     const folder = DB.GetFile(id);
        //     if (folder) {
        //         node.folders.push({ name: folder.name, id: folder.id });
        //         if (folder.parentFolderId) {
        //             parentIds.push([...folder.parentFolderId]);
        //         }
        //     }
        // }

        // hierarchy.push(node);

        // hierarchy.push({
        //     level: 0,
        //     name: base.name,
        //     id: base.id,
        // });

        // while (parentId !== undefined) {
        //     const folder = this.GetFile(parentId);
        //     if (!folder) {
        //         break;
        //     }

        //     parentId = folder?.parentFolderId;
        //     hierarchy.push({
        //         level,
        //         name: folder.name,
        //         id: folder.id,
        //     });

        //     level++;
        // }

        // Root
        // hierarchy.push({
        //     level,
        //     folders: [{ name: '', id: '' }],
        // });

        return hierarchy;
    }

    static CreateDocumentRecord(name: string, type: string, size: number, parentFolderId?: string): FileRecord {
        const user = this.Users.find((u) => u.id === '7ea16ac1-99cf-478a-80b1-7f25458522e5') as User;
        const file: FileRecord = {
            locked: false,
            name,
            mimeType: type,
            recordType: FileRecordTypeEnum.DOCUMENT,
            bytes: size,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            },
            id: v4(),
            parentFolderId: parentFolderId ? [parentFolderId] : undefined,
            status: FileStatusEnum.CHECKED_IN,
            history: [],
            hidden: false,
        };
        this.Files.push(file);
        return file;
    }

    static CreateReferenceRecord(name: string, sourceId: string, parentFolderId?: string): FileRecord {
        const original = this.Files.find((f) => f.id === sourceId);

        if (!original) {
            throw new Error('No original file record was found with the given sourceId.');
        }

        if (
            this.Files.filter((f) => f.parentFolderId?.includes(parentFolderId as string)).find(
                (f) => f.name.toLowerCase() === name.toLowerCase()
            )
        ) {
            throw new Error('A file record with this name already exists.');
        }

        const record: FileRecord = {
            id: v4(),
            createdAt: new Date(),
            updatedAt: new Date(),
            recordType: FileRecordTypeEnum.REFERENCE,
            status: original.recordType === FileRecordTypeEnum.DOCUMENT ? original.status : FileStatusEnum.NOT_APPLICABLE,
            name,
            source: { id: original.id, recordType: original.recordType, name: original.name, link: original.link },
            parentFolderId: parentFolderId ? [parentFolderId] : [],
            locked: false,
            history: [
                {
                    createdAt: new Date(),
                    message: `Created reference (${name}) to original: ${original.name}`,
                },
            ],
            hidden: false,
        };
        this.Files.push(record);
        return record;
    }

    static CreateLinkRecord(name: string, link: string, parentFolderId: string[]): FileRecord {
        if (!name) {
            throw new Error('A name is required.');
        }
        if (this.Files.find((f) => f.name.toLowerCase() === name.toLowerCase() && f.parentFolderId?.at(0) === parentFolderId.at(0))) {
            throw new Error('A file record with this name already exists.');
        }

        const record: FileRecord = {
            id: v4(),
            createdAt: new Date(),
            updatedAt: new Date(),
            name,
            link,
            parentFolderId,
            history: [{ createdAt: new Date(), message: `Created link ${name} to ${link}` }],
            status: FileStatusEnum.NOT_APPLICABLE,
            recordType: FileRecordTypeEnum.LINK,
            createdBy: undefined,
            hidden: false,
            locked: false,
        };
        this.Files.push(record);
        return record;
    }

    static UpdateWorksheetChecklistItemStatus(
        worksheetId: string,
        checklistItemId: string,
        status: WorksheetChecklistItemStatusEnum
    ): void {
        const worksheet = this.Worksheets.find((ws) => ws.id === worksheetId);
        if (!worksheet) {
            return;
        }

        this.WorksheetChecklistItems = this.WorksheetChecklistItems.map((wci) => {
            if (wci.id !== checklistItemId) {
                return wci;
            }
            return { ...wci, status };
        });
    }

    static EnsureUniqueFileRecordName(name: string, parentId: string): string {
        let recordName = name;
        const records = this.Files.filter((f) => f.parentFolderId?.includes(parentId));
        const existingRecord = records.find((r) => r.name.toLowerCase() === recordName.toLowerCase());
        if (existingRecord) {
            let i = 1;
            while (records.find((r) => r.name.toLowerCase() === `${recordName.toLowerCase()} (${i})`)) {
                i += 1;
            }
            recordName = `${recordName} (${i})`;
        }

        return recordName;
    }

    static GetReferences(sourceId: string): FileRecord[] {
        return this.Files.filter((f) => f?.source?.id === sourceId);
    }

    static CreateFolder(id: string, name: string, system: boolean = false, parent?: string[], hidden = false): FileRecord {
        const folder = {
            id: id,
            parentFolderId: parent,
            createdAt: new Date(),
            updatedAt: new Date(),
            system,
            itemCount: 0,
            recordType: FileRecordTypeEnum.FOLDER,
            status: FileStatusEnum.NOT_APPLICABLE,
            name: name,
            history: [],
            hidden,
            locked: false,
        };

        this.Files.push(folder);

        return folder;
    }

    static GetWorksheetEntries(worksheetId: string, deleted?: boolean): WorksheetEntry[] {
        const entries = this.WorksheetEntries.filter((e) => e.worksheetId === worksheetId);
        if (deleted) {
            return entries;
        }
        return entries.filter((e) => e.status === WorksheetEntryStatusEnum.ACTIVE);
    }

    static GetOtherScheduleEntries(scheduleId: string, deleted?: boolean): OtherScheduleEntry[] {
        const entries = this.OtherScheduleEntries.filter((e) => e.otherScheduleId === scheduleId);
        if (deleted) {
            return entries;
        }
        return entries.filter((e) => e.status === OtherScheduleEntryStatusEnum.ACTIVE);
    }

    static CreateWorksheetEntry(
        worksheetId: string,
        entry: { date: string; amount: number; nature: NatureEnum; description?: string; files: string[] }
    ): WorksheetEntry {
        const existingEntriesByReference = this.GetWorksheetEntries(worksheetId).sort((a, b) => (a.reference > b.reference ? -1 : 1));
        const reference = existingEntriesByReference[0]?.reference || 0;

        const existingEntriesBySequence = this.GetWorksheetEntries(worksheetId).sort((a, b) => (a.sequence > b.sequence ? -1 : 1));
        const sequence = existingEntriesBySequence[0]?.sequence || 0;

        const _entry: WorksheetEntry = {
            id: v4(),
            description: entry.description,
            date: new Date(entry.date).toISOString(),
            amount: entry.amount,
            nature: entry.nature,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            files: entry.files,
            reference: reference + 1,
            sequence: sequence + 1,
            worksheetId,
            workpaperId: '',
            status: WorksheetEntryStatusEnum.ACTIVE,
        };

        this.WorksheetEntries.push(_entry);

        return _entry;
    }

    static CreateOtherScheduleEntry(
        scheduleId: string,
        entry: { date: string; description?: string; files: string[] }
    ): OtherScheduleEntry {
        const existingEntriesByReference = this.GetOtherScheduleEntries(scheduleId).sort((a, b) => (a.reference > b.reference ? -1 : 1));
        const reference = existingEntriesByReference[0]?.reference || 0;

        const existingEntriesBySequence = this.GetOtherScheduleEntries(scheduleId).sort((a, b) => (a.sequence > b.sequence ? -1 : 1));
        const sequence = existingEntriesBySequence[0]?.sequence || 0;

        const _entry: OtherScheduleEntry = {
            id: v4(),
            description: entry.description,
            date: new Date(entry.date),
            createdAt: new Date(),
            updatedAt: new Date(),
            files: entry.files,
            reference: reference + 1,
            sequence: sequence + 1,
            otherScheduleId: scheduleId,
            status: OtherScheduleEntryStatusEnum.ACTIVE,
        };

        this.OtherScheduleEntries.push(_entry);

        return _entry;
    }

    static UpdateWorksheetVariance(worksheetId: string): void {
        const worksheet = this.Worksheets.find((ws) => ws.id === worksheetId) as ReconciliationWorksheet;
        const entries = this.GetWorksheetEntries(worksheetId);
        // Calculate subtotal
        const subtotal = this.calcSubtotal(worksheet.nature, entries);

        let current = 0;

        if (worksheet.workpaperDebitTY) {
            current = worksheet.nature === 'DEBIT' ? worksheet.workpaperDebitTY : worksheet.workpaperDebitTY * -1;
        } else if (worksheet.workpaperCreditTY) {
            current = worksheet.nature === 'DEBIT' ? worksheet.workpaperCreditTY * -1 : worksheet.workpaperCreditTY;
        }

        // const unreconciledAmount = subtotal - current;
        const unreconciledAmount = current - subtotal;

        if (unreconciledAmount === 0) {
            worksheet.unreconciledCr = undefined;
            worksheet.unreconciledDr = undefined;
        } else {
            if (worksheet.nature === 'DEBIT') {
                if (unreconciledAmount < 0) {
                    worksheet.unreconciledCr = Math.abs(unreconciledAmount);
                    worksheet.unreconciledDr = undefined;
                } else {
                    worksheet.unreconciledDr = Math.abs(unreconciledAmount);
                    worksheet.unreconciledCr = undefined;
                }
            }

            if (worksheet.nature === 'CREDIT') {
                if (unreconciledAmount < 0) {
                    worksheet.unreconciledDr = Math.abs(unreconciledAmount);
                    worksheet.unreconciledCr = undefined;
                } else {
                    worksheet.unreconciledCr = Math.abs(unreconciledAmount);
                    worksheet.unreconciledDr = undefined;
                }
            }
        }
    }

    static UpdateWorksheetEntry(entryId: string, fields: Partial<WorksheetEntry>): void {
        this.WorksheetEntries = this.WorksheetEntries.map((e) => {
            if (e.id !== entryId) {
                return e;
            }
            return { ...e, ...fields };
        });
    }

    static UpdateOtherScheduleEntry(entryId: string, fields: Partial<OtherScheduleEntry>): void {
        this.OtherScheduleEntries = this.OtherScheduleEntries.map((e) => {
            if (e.id !== entryId) {
                return e;
            }
            return { ...e, ...fields, updatedAt: new Date() };
        });
    }

    static DeleteWorksheetEntry(entryId: string): void {
        this.WorksheetEntries = this.WorksheetEntries.map((we) => {
            if (we.id !== entryId) {
                return we;
            }
            return { ...we, status: WorksheetEntryStatusEnum.DELETED };
        });
    }

    static DeleteOtherScheduleEntry(entryId: string): void {
        this.OtherScheduleEntries = this.OtherScheduleEntries.map((we) => {
            if (we.id !== entryId) {
                return we;
            }
            return { ...we, status: OtherScheduleEntryStatusEnum.DELETED };
        });
    }

    static GetInternalComments(workpaperId: string): InternalCommentOverview[] {
        return this.InternalComments.filter((ic) => ic.workpaperId === workpaperId).map((ic) => {
            const user = this.Users.find((u) => u.id === ic.createdBy.id) as User;
            const worksheet = this.Worksheets.find((ws) => ws.id === ic.worksheet?.id);
            const otherSchedule = this.OtherSchedules.find((s) => s.id === ic.otherSchedule?.id);
            return {
                ...ic,
                createdBy: {
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    avatarUrl: user.avatarUrl,
                    profileHexColour: user.profileHexColour,
                },
                worksheet: worksheet ? { id: worksheet.id, code: worksheet.code, description: worksheet.description } : undefined,
                otherSchedule: otherSchedule ? { id: otherSchedule.id, name: otherSchedule.name } : undefined,
                replies: ic.replies.map((r) => r.id),
            };
        });
    }

    static GetInternalComment(id: string): InternalComment | undefined {
        return this.InternalComments.filter((ic) => ic.id === id).map((ic) => {
            const user = this.Users.find((u) => u.id === ic.createdBy.id) as User;
            const worksheet = this.Worksheets.find((ws) => ws.id === ic.worksheet?.id);
            const otherSchedule = this.OtherSchedules.find((s) => s.id === ic.otherSchedule?.id);
            return {
                ...ic,
                createdBy: {
                    profileHexColour: user.profileHexColour,
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    avatarUrl: user.avatarUrl,
                },
                worksheet: worksheet ? { id: worksheet.id, code: worksheet.code, description: worksheet.description } : undefined,
                otherSchedule: otherSchedule ? { id: otherSchedule.id, name: otherSchedule.name } : undefined,
                replies: ic.replies.map((r) => {
                    const user = this.Users.find((u) => u.id === r.createdBy.id) as User;
                    return {
                        ...r,
                        createdBy: {
                            id: user.id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            avatarUrl: user.avatarUrl,
                            profileHexColour: user.profileHexColour,
                        },
                    };
                }),
            };
        })[0];
    }

    static DeleteInternalComment(id: string): void {
        this.InternalComments = this.InternalComments.filter((ic) => ic.id !== id);
    }

    static CreateInternalComment(workpaperId: string, rawComment: string, worksheetId?: string, otherScheduleId?: string): InternalComment {
        const internalComment: InternalComment = {
            id: v4(),
            workpaperId,
            rawComment,
            worksheet: worksheetId ? { id: worksheetId, code: '', description: '' } : undefined,
            otherSchedule: otherScheduleId ? { id: otherScheduleId, name: '' } : undefined,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', lastName: '', firstName: '', profileHexColour: '' },
            replies: [],
            status: InternalCommentStatusEnum.ACTIVE,
        };
        this.InternalComments.push(internalComment);

        const user = this.Users.find((u) => u.id === internalComment.createdBy.id) as User;

        return {
            ...internalComment,
            createdBy: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            },
        };
    }

    static CreateInternalCommentReply(threadId: string, rawReply: string): Reply {
        const questionReply: Reply = {
            id: v4(),
            threadId,
            rawReply,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', lastName: '', firstName: '', profileHexColour: '' },
        };
        this.Replies.push(questionReply);

        const user = this.Users.find((u) => u.id === questionReply.createdBy.id) as User;

        const internalComment = this.InternalComments.find((i) => i.id === threadId) as InternalComment;

        internalComment.updatedAt = new Date();
        internalComment.replies.push(questionReply);

        return {
            ...questionReply,
            createdBy: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            },
        };
    }

    static DeleteInternalCommentReply(id: string): void {
        this.InternalComments = this.InternalComments.map((ic) => {
            return {
                ...ic,
                replies: ic.replies.filter((r) => r.id !== id),
            };
        });
    }

    static UpdateInternalComment(id: string, fields: Partial<InternalComment>): InternalComment | undefined {
        const comment = this.InternalComments.find((ic) => ic.id === id);
        if (!comment) {
            return;
        }

        if (fields.status) {
            comment.status = fields.status;
            comment.resolvedBy =
                fields.status === InternalCommentStatusEnum.RESOLVED
                    ? { id: '78e94165-4ef7-4d7d-a88c-5b878f317c02', firstName: '', lastName: '' }
                    : undefined;
        }
        comment.updatedAt = new Date();
        this.InternalComments = this.InternalComments.map((ic) => {
            if (ic.id !== id) {
                return ic;
            }
            return { ...comment };
        });
        return comment;
    }

    static UpdateInternalCommentReply(commentId: string, id: string, message: string): InternalComment | undefined {
        const internalComment = this.InternalComments.find((i) => i.id === commentId) as InternalComment;

        internalComment.updatedAt = new Date();
        internalComment.replies.map((r) => {
            if (r.id === id) {
                r.rawReply = message;
                r.updatedAt = new Date();
            }
            return r;
        });

        return internalComment;
    }

    static CalculateWorksheetStatus(worksheetId: string, status?: WorksheetStatusEnum): WorksheetStatusEnum {
        const worksheet = this.GetWorksheet(worksheetId) as ReconciliationWorksheet;
        const originalStatus = worksheet.status;

        if (status) {
            worksheet.status = status;
        } else {
            const checklistComplete = worksheet.checklist.every((item) => item.status !== WorksheetChecklistItemStatusEnum.UNCHECKED);
            const reconciled = !worksheet.unreconciledDr && !worksheet.unreconciledCr;

            if (checklistComplete && reconciled) {
                worksheet.status = WorksheetStatusEnum.AWAITING_REVIEW;
            } else {
                worksheet.status = WorksheetStatusEnum.UNPREPARED;
            }
        }

        if (originalStatus !== worksheet.status) {
            setTimeout(() => {
                DB.AddWorksheetHistoryEvent(
                    WorksheetHistoryEventEnum.STATUS_CHANGE,
                    `Status changed to ${formatMixedCase(worksheet.status)}`,
                    worksheet.id,
                    true
                );
            }, 1);
        }

        return worksheet.status;
    }

    static CalculateOtherScheduleStatus(scheduleId: string, status?: OtherScheduleStatusEnum): void {
        const schedule = this.OtherSchedules.find((os) => os.id === scheduleId) as OtherSchedule;
        const originalStatus = schedule.status;

        if (status) {
            schedule.status = status;
        }

        if (originalStatus !== schedule.status) {
            setTimeout(() => {
                DB.AddOtherScheduleHistoryEvent(
                    OtherScheduleHistoryEvent.STATUS_CHANGE,
                    `Status changed to ${formatMixedCase(schedule.status)}`,
                    schedule.id,
                    true
                );
            }, 1);
        }
    }

    private static calcSubtotal(worksheetNature: NatureEnum, entries: WorksheetEntry[] = []): number {
        const total = entries.reduce((acc, entry) => {
            if (entry.nature === NatureEnum.CREDIT) {
                return acc + (worksheetNature === 'CREDIT' ? entry.amount : entry.amount * -1);
            }
            return acc + (worksheetNature === 'DEBIT' ? entry.amount || 0 : (entry.amount || 0) * -1);
        }, 0);

        return total;
    }

    static GetClientQueries(workpaperId: string): ClientQueryOverview[] {
        return this.ClientQueries.filter((cq) => cq.workpaperId === workpaperId).map((cq) => {
            const user = this.Users.find((u) => u.id === cq.createdBy.id) as User;
            const worksheet = this.Worksheets.find((ws) => ws.id === cq.worksheet?.id);
            const otherSchedule = this.OtherSchedules.find((s) => s.id === cq.otherSchedule?.id);
            return {
                ...cq,
                createdBy: {
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    avatarUrl: user.avatarUrl,
                    profileHexColour: user.profileHexColour,
                },
                worksheet: worksheet ? { id: worksheet.id, code: worksheet.code, description: worksheet.description } : undefined,
                otherSchedule: otherSchedule ? { id: otherSchedule.id, name: otherSchedule.name } : undefined,
                replies: cq.replies.map((r) => r.id),
            };
        });
    }

    static GetClientQuery(id: string): ClientQuery | undefined {
        return this.ClientQueries.filter((cq) => cq.id === id).map((cq) => {
            const user = this.Users.find((u) => u.id === cq.createdBy.id) as User;
            const worksheet = this.Worksheets.find((ws) => ws.id === cq.worksheet?.id);
            const otherSchedule = this.OtherSchedules.find((s) => s.id === cq.otherSchedule?.id);
            return {
                ...cq,
                createdBy: {
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    avatarUrl: user.avatarUrl,
                    profileHexColour: user.profileHexColour,
                },
                worksheet: worksheet ? { id: worksheet.id, code: worksheet.code, description: worksheet.description } : undefined,
                otherSchedule: otherSchedule ? { id: otherSchedule.id, name: otherSchedule.name } : undefined,
                replies: cq.replies.map((r) => {
                    const user = this.Users.find((u) => u.id === r.createdBy.id) as User;
                    return {
                        ...r,
                        createdBy: {
                            id: user.id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            avatarUrl: user.avatarUrl,
                            profileHexColour: user.profileHexColour,
                        },
                    };
                }),
            };
        })[0];
    }

    static CreateClientQuery(workpaperId: string, rawQuery: string, worksheetId?: string, otherScheduleId?: string): ClientQuery {
        const clientQuery: ClientQuery = {
            id: v4(),
            workpaperId,
            rawQuery,
            worksheet: worksheetId ? { id: worksheetId, code: '', description: '' } : undefined,
            otherSchedule: otherScheduleId ? { id: otherScheduleId, name: '' } : undefined,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', lastName: '', firstName: '', profileHexColour: '' },
            replies: [],
            status: ClientQueryStatusEnum.DRAFT,
        };
        this.ClientQueries.push(clientQuery);

        const user = this.Users.find((u) => u.id === clientQuery.createdBy.id) as User;

        return {
            ...clientQuery,
            createdBy: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            },
        };
    }

    static CreateClientQueryReply(threadId: string, rawReply: string): Reply {
        const questionReply: Reply = {
            id: v4(),
            threadId,
            rawReply,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: { id: '7ea16ac1-99cf-478a-80b1-7f25458522e5', lastName: '', firstName: '', profileHexColour: '' },
        };
        this.Replies.push(questionReply);

        const user = this.Users.find((u) => u.id === questionReply.createdBy.id) as User;

        const clientQuery = this.ClientQueries.find((cq) => cq.id === threadId) as ClientQuery;

        clientQuery.updatedAt = new Date();
        clientQuery.replies.push(questionReply);

        return {
            ...questionReply,
            createdBy: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
                profileHexColour: user.profileHexColour,
            },
        };
    }

    static DeleteClientQueryReply(id: string): void {
        this.ClientQueries = this.ClientQueries.map((cq) => {
            return {
                ...cq,
                replies: cq.replies.filter((r) => r.id !== id),
            };
        });
    }

    static UpdateClientQueryReply(queryId: string, id: string, message: string): ClientQuery | undefined {
        const clientQuery = this.ClientQueries.find((cq) => cq.id === queryId) as ClientQuery;

        clientQuery.updatedAt = new Date();
        clientQuery.replies.map((cq) => {
            if (cq.id === id) {
                cq.rawReply = message;
                cq.updatedAt = new Date();
            }
            return cq;
        });

        return clientQuery;
    }

    static UpdateClientQuery(id: string, fields: Partial<ClientQuery>): ClientQuery | undefined {
        const query = this.ClientQueries.find((cq) => cq.id === id);
        if (!query) {
            return;
        }

        if (fields.status) {
            query.status = fields.status;
            query.resolvedBy =
                fields.status === ClientQueryStatusEnum.RESOLVED
                    ? { id: '78e94165-4ef7-4d7d-a88c-5b878f317c02', firstName: '', lastName: '' }
                    : undefined;
        }
        query.updatedAt = new Date();
        this.ClientQueries = this.ClientQueries.map((cq) => {
            if (cq.id !== id) {
                return cq;
            }
            return { ...query };
        });
        return query;
    }

    static DeleteClientQuery(id: string): void {
        this.ClientQueries = this.ClientQueries.filter((cq) => cq.id !== id);
    }

    static AddOtherScheduleHistoryEvent(type: OtherScheduleHistoryEvent, message: string, otherScheduleId: string, system?: boolean): void {
        this.OtherScheduleHistory.push({
            id: v4(),
            otherScheduleId,
            createdAt: new Date(),
            createdBy: system ? 'SYSTEM' : 'Cimplico Demo',
            eventType: type,
            message,
        });
    }

    static AddWorksheetHistoryEvent(type: WorksheetHistoryEventEnum, message: string, worksheetId: string, system?: boolean): void {
        this.WorksheetHistory.push({
            id: v4(),
            worksheetId,
            createdAt: new Date(),
            createdBy: system ? 'SYSTEM' : 'Marley Culhane',
            type,
            message,
        });
    }
}
